import { Dispatchers, toOption } from '@client/new/helpers/types';
import {
  ContentRootTree,
  ContentTree,
  Content,
  ContentType,
  ExtrasContent,
  ContentVideo,
  ContentLink,
  PurchaseInfo,
} from '../model/extras.domain';
import { ExtrasVMActionMap, Filters } from './extras.viewModel';
import { BasicMenuItem } from '@client/new/components/BasicMenu/BasicMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilePdf,
  faImage,
  faLink,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons';
import { CreateContentInput, UpdateContentInput } from '../model/extras.model';
import { pickObject } from '@client/new/helpers/general';
import { SuperTooltipTemplateId } from '@client/new/components/SuperTooltip/generated';

export const filterTypeOptions = [
  toOption('ContentLink', 'Links'),
  toOption('ContentLegacyGuide', 'Legacy Guides'),
  toOption('ContentSlides', 'Slides'),
  toOption('ContentPDF', 'PDFs'),
  toOption('ContentVideo', 'Videos'),
];

export const filterAccessOptions = [
  toOption('AnyPurchase', 'Any Purchase'),
  toOption('SubscriptionOnly', 'Subscriptions'),
  toOption('BundlesOnly', 'Offers'),
];

export const filterVisibilityOptions = [
  toOption('visible', 'Visible'),
  toOption('not-visible', 'Not Visible'),
  toOption('archived', 'Archived'),
];
export const getAvailableFilterOptions = (
  contentMap: ExtrasContent['contentMap'],
) => {
  const hasLegacyGuides = Object.values(contentMap).some(
    (content) => content.type === 'ContentLegacyGuide',
  );

  return filterTypeOptions.filter(
    (option) => option.value !== 'ContentLegacyGuide' || hasLegacyGuides,
  );
};

export const typeToLabel = (type: ContentType): string => {
  switch (type) {
    case 'ContentLegacyGuide':
      return 'Guide';
    case 'ContentLink':
      return 'Link';
    case 'ContentPDF':
      return 'PDF';
    case 'ContentSlides':
      return 'Slides';
    case 'ContentVideo':
      return 'Video';
    default:
      return '';
  }
};

const mixpanelTypeMap: Partial<Record<ContentType, string>> = {
  ContentLink: 'Open Url',
  ContentPDF: 'PDF',
  ContentSlides: 'Slides',
  ContentVideo: 'Video',
};

export const toMixpanelContentType = (type: ContentType): string =>
  mixpanelTypeMap[type] ?? type;

export const typeSuperTooltipId = {
  ContentPDF: 'extras-pdf',
  ContentSlides: 'extras-slides',
  ContentLink: 'extras-links',
  ContentVideo: 'extras-video',
} satisfies Partial<Record<ContentType, SuperTooltipTemplateId | undefined>>;

export const typeToModalTitle = (type: ContentType): string => {
  switch (type) {
    case 'ContentLegacyGuide':
      return 'Guide';
    case 'ContentLink':
      return 'Link';
    case 'ContentPDF':
      return 'PDF Document';
    case 'ContentSlides':
      return 'Slides';
    case 'ContentVideo':
      return 'Video';
    default:
      return '';
  }
};

export function toLegacyGuideURL(id: number): string {
  return `https://fitner-web.s3.amazonaws.com/hc/nutrition/${id}/cover.jpg`;
}

export const filterExtrasVM = (
  vm: ExtrasContent,
  search: string,
  filters: Filters,
): ExtrasContent['contentMap'] => {
  const { contentMap } = vm;

  const filteredContentMap: Record<string, Content> = Object.values(contentMap)
    .filter((item) => {
      // Always show tabs (parent containers)
      if (item.type === 'ContentTab') return true;

      const { purchaseInfo } = item.value;

      // Filter by type (e.g., ContentVideo, ContentPDF)
      const matchesType = !filters.type || item.type === filters.type;

      // Filter by name/title
      const title = item.value.title || '';
      const matchesSearch =
        !search || title.toLowerCase().includes(search.toLowerCase());

      // Filter by access method
      let matchesAccess = !filters.access;
      if (filters.access && purchaseInfo.type === 'purchasable') {
        if (filters.access === 'AnyPurchase') {
          // Default access method (subscription only, no bundles)
          matchesAccess = purchaseInfo.accessMethod === 'Default';
        } else if (filters.access === 'SubscriptionOnly') {
          // SubscriptionAndBundles access method (can have offers or not)
          matchesAccess =
            purchaseInfo.accessMethod === 'SubscriptionAndBundles';
        } else if (filters.access === 'BundlesOnly') {
          // Items with BundlesOnly access method
          matchesAccess = purchaseInfo.accessMethod === 'BundlesOnly';
        }
      }

      // Filter by visibility (publication status)
      let matchesVisibility = !filters.visibility;
      if (filters.visibility && purchaseInfo.type === 'purchasable') {
        // Map filter values to publicationStatus values
        if (filters.visibility === 'visible') {
          matchesVisibility = purchaseInfo.publicationStatus === 'published';
        } else if (filters.visibility === 'not-visible') {
          matchesVisibility = purchaseInfo.publicationStatus === 'draft';
        } else if (filters.visibility === 'archived') {
          matchesVisibility = purchaseInfo.publicationStatus === 'archived';
        }
      }

      return matchesType && matchesSearch && matchesAccess && matchesVisibility;
    })
    .reduce<Record<string, Content>>((acc, item) => {
      acc[item.value.id] = item;
      return acc;
    }, {});

  return filteredContentMap;
};

/**
 * Filters the content tree structure based on a filtered content map.
 * Only keeps nodes whose IDs exist in the filtered map.
 * Recursively filters child nodes as well.
 */
export const filterContentTree = (
  tree: ContentRootTree | ContentTree,
  filteredMap: Record<string, Content>,
): ContentRootTree | ContentTree => {
  // If no items, return tree as-is
  if (!tree.items || tree.items.length === 0) {
    return tree;
  }

  // Filter items: keep only those that exist in filteredMap OR have children that exist
  const filteredItems = tree.items
    .map((item) => {
      // Recursively filter children
      const filteredItem = filterContentTree(item, filteredMap);

      // Keep item if:
      // 1. It exists in filteredMap, OR
      // 2. It has children after filtering (parent node)
      const existsInMap = filteredMap[item.id] !== undefined;
      const hasChildren = filteredItem.items && filteredItem.items.length > 0;

      if (existsInMap || hasChildren) {
        return filteredItem;
      }

      return null;
    })
    .filter((item): item is ContentTree => item !== null);

  return {
    ...tree,
    items: filteredItems,
  };
};

export const toExtrasMenuItems = (
  dispatch: Dispatchers<ExtrasVMActionMap>['dispatch'],
): BasicMenuItem[] => [
  {
    label: 'Link',
    onClick: () => dispatch('Create', 'ContentLink'),
    icon: <FontAwesomeIcon icon={faLink} />,
  },
  {
    label: 'Slides',
    onClick: () => dispatch('Create', 'ContentSlides'),
    icon: <FontAwesomeIcon icon={faImage} />,
  },
  {
    label: 'PDF',
    onClick: () => dispatch('Create', 'ContentPDF'),
    icon: <FontAwesomeIcon icon={faFilePdf} />,
  },
  {
    label: 'Video',
    onClick: () => dispatch('Create', 'ContentVideo'),
    icon: <FontAwesomeIcon icon={faVideo} />,
  },
];

type FormValues = {
  [K in Content as K['type']]: K['value'];
};

const defaultPurchaseInfo: PurchaseInfo = {
  type: 'not-purchasable',
};

export const defaultValues: FormValues = {
  ['ContentLegacyGuide']: {
    purchaseInfo: defaultPurchaseInfo,
    title: '',
    contentId: 0,
    count: 0,
    id: '',
  },
  ['ContentLink']: {
    purchaseInfo: defaultPurchaseInfo,
    title: '',
    url: '',
    coverImage: '',
    id: '',
  },
  ['ContentTab']: {
    purchaseInfo: defaultPurchaseInfo,
    id: '',
    name: '',
  },
  ['ContentSlides']: {
    purchaseInfo: defaultPurchaseInfo,
    id: '',
    title: '',
    coverURL: '',
    downloadable: false,
  },
  ['ContentPDF']: {
    purchaseInfo: defaultPurchaseInfo,
    id: '',
    title: '',
    url: '',
    coverURL: '',
    downloadable: false,
    fileName: '',
    fileSize: 0,
  },
  ['ContentImage']: {
    purchaseInfo: defaultPurchaseInfo,
    id: '',
    title: '',
    url: '',
    file: null,
  },
  ['ContentVideo']: {
    purchaseInfo: defaultPurchaseInfo,
    id: '',
    allowComments: false,
    commentsCount: 0,
    coverURL: null,
    createdAt: 0,
    description: null,
    downloadable: false,
    durationMs: 0,
    isProcessed: false,
    isPublished: false,
    libraryVideoId: '',
    likesCount: 0,
    muxAssetId: '',
    muxPlaybackId: '',
    thumbnailCustomURL: null,
    thumbnailTimestampMs: 0,
    title: '',
    type: '',
    updatedAt: null,
    watchCount: 0,
  },
};

export const togglePublicationStatus = <T extends Content>(content: T): T => {
  if (content.value.purchaseInfo.type === 'not-purchasable') {
    return content; // Can't toggle publication status for not-purchasable items
  }

  const currentStatus = content.value.purchaseInfo.publicationStatus;
  const newStatus: 'draft' | 'published' =
    currentStatus === 'published' ? 'draft' : 'published';

  return {
    ...content,
    value: {
      ...content.value,
      purchaseInfo: {
        ...content.value.purchaseInfo,
        publicationStatus: newStatus,
      },
    },
  };
};

const copyExcluding = (obj: Record<string, unknown>, excludeKeys: string[]) => {
  const result: Record<string, unknown> = {};
  Object.keys(obj).forEach((key) => {
    if (!excludeKeys.includes(key)) {
      result[key] = obj[key];
    }
  });
  return result;
};

export function updateTreeItems(
  tree: ContentRootTree | ContentTree,
  targetId: string,
  newItems: ContentTree[],
): ContentRootTree | ContentTree {
  const updateNode = (node: ContentTree | ContentRootTree): ContentTree => {
    const isTarget = node.id === targetId;

    return {
      id: node.id,
      ...copyExcluding(node, ['id', 'items']),
      ...(node.items?.length && {
        items: isTarget
          ? newItems.map((newItem) => {
              return updateNode({
                ...newItem,
                items:
                  node.items?.find((existing) => existing.id === newItem.id)
                    ?.items || newItem.items,
              });
            })
          : node.items.map((child) => updateNode(child)),
      }),
    };
  };

  return updateNode(tree);
}

export const getContentName = (c: Content): string => {
  switch (c.type) {
    case 'ContentLegacyGuide':
      return c.value.title || 'Untitled Guide';
    case 'ContentLink':
      return c.value.title || 'Untitled Link';
    case 'ContentTab':
      return c.value.name || 'Untitled Tab';
    case 'ContentPDF':
      return c.value.title || 'Untitled PDF';
    case 'ContentSlides':
      return c.value.title || 'Untitled Slides';
    case 'ContentVideo':
      return c.value.title || 'Untitled Video';
    default:
      return 'Unknown Content';
  }
};

const getPublishedField = (
  purchaseInfo: PurchaseInfo,
): { isPublished?: boolean } => {
  if (
    purchaseInfo.type === 'purchasable' &&
    purchaseInfo.accessMethod === 'Default'
  ) {
    return {
      isPublished: purchaseInfo.publicationStatus === 'published',
    };
  }
  return {};
};

export const toUpdateContentInput = (content: Content): UpdateContentInput => {
  const { id: _id, purchaseInfo: _purchaseInfo, ..._value } = content.value;
  const publishedField = getPublishedField(content.value.purchaseInfo);

  switch (content.type) {
    case 'ContentLegacyGuide':
      return {
        type: 'ContentLegacyGuide',
        value: {
          ...pickObject(content.value, ['title', 'contentId', 'count']),
          ...publishedField,
        },
      };
    case 'ContentLink':
      return {
        type: 'ContentLink',
        value: {
          ...pickObject(content.value, ['title', 'url', 'coverImage']),
          ...publishedField,
        },
      };
    case 'ContentTab':
      return {
        type: 'ContentTab',
        value: pickObject(content.value, ['name']),
      };
    case 'ContentSlides':
      return {
        type: 'ContentSlides',
        value: {
          ...pickObject(content.value, ['title', 'coverURL', 'downloadable']),
          ...publishedField,
        },
      };
    case 'ContentVideo':
      return {
        type: 'ContentVideo',
        value: {
          ...pickObject(content.value, [
            'allowComments',
            'coverURL',
            'description',
            'downloadable',
            'thumbnailCustomURL',
            'thumbnailTimestampMs',
            'libraryVideoId',
            'title',
            'isProcessed',
          ]),
          ...publishedField,
        },
      };
    case 'ContentPDF':
      return {
        type: 'ContentPDF',
        value: {
          ...pickObject(content.value, [
            'title',
            'coverURL',
            'downloadable',
            'fileName',
          ]),
          ...publishedField,
        },
      };

    default:
      throw new Error(`Unsupported content type ${content.type}`);
  }
};

export const toCreateContentInput = (
  content: Content,
  parentId: string,
): CreateContentInput => {
  switch (content.type) {
    case 'ContentLink': {
      const linkValue = content.value as ContentLink['value'] & {
        isPublished?: boolean;
      };
      return {
        type: 'ContentLink',
        value: {
          id: linkValue.id,
          parentId,
          title: linkValue.title,
          url: linkValue.url,
          coverImage: linkValue.coverImage,
          isPublished: linkValue.isPublished ?? false,
        },
      };
    }
    case 'ContentTab':
      return {
        type: 'ContentTab',
        value: {
          id: content.value.id,
          name: content.value.name,
          parentId: parentId === 'empty' ? '' : parentId,
        },
      };
    case 'ContentSlides':
      return {
        type: 'ContentSlides',
        value: {
          id: content.value.id,
          parentId,
          coverURL: content.value.coverURL || '',
          title: content.value.title || '',
          downloadable: content.value.downloadable,
        },
      };
    case 'ContentPDF':
      return {
        type: 'ContentPDF',
        value: {
          id: content.value.id,
          parentId,
          title: content.value.title || '',
          coverURL: content.value.coverURL || '',
          downloadable: content.value.downloadable,
          fileName: content.value.fileName,
        },
      };
    case 'ContentLegacyGuide':
      return {
        type: 'ContentLegacyGuide',
        value: {
          id: content.value.id,
          parentId,
          title: content.value.title,
          contentId: content.value.contentId,
          count: content.value.count,
        },
      };

    default:
      throw new Error(`Unsupported content type ${content.type}`);
  }
};

export const getPending = (
  c: ExtrasContent['contentMap'],
): ContentVideo['value'][] => {
  return Object.values(c)
    .filter((c) => c.type === 'ContentVideo' && !c.value.isProcessed)
    .map((c) => c.value as ContentVideo['value']);
};
